import { ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useStickyToolbarRegister } from "@/hooks/useStickyToolbar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface BulkActionsBarProps {
  selectedCount: number;
  onClearSelection: () => void;
  children: ReactNode;
  /**
   * AUT-419: pin the bar to the top of the viewport, stacked directly above the sticky table header,
   * so it stays available while scrolling. Requires a StickyToolbarProvider around the bar + table.
   */
  sticky?: boolean;
}

export function BulkActionsBar({
  selectedCount,
  onClearSelection,
  children,
  sticky = false,
}: BulkActionsBarProps) {
  const register = useStickyToolbarRegister();
  if (selectedCount === 0) return null;

  const bar = (
    <div className="flex items-center gap-3 px-3 py-2 bg-primary-tint-soft rounded-md animate-fade-in">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="text-xs h-7 gap-1">
            Bulk Actions
            <ChevronDown className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="bg-popover z-50 rounded-[8px]"
        >
          {children}
        </DropdownMenuContent>
      </DropdownMenu>

      <span className="text-xs font-normal text-foreground">
        {selectedCount} selected
      </span>

      <Button
        variant="ghost"
        size="sm"
        className="text-xs h-7 text-primary ml-auto"
        onClick={onClearSelection}
      >
        Clear selection
      </Button>
    </div>
  );

  if (!sticky) return bar;

  // AUT-419: pinned, matching the prototype — an opaque card wrapper with top/bottom bleed masks so
  // rows scrolling under the bar never peek at its edges.
  return (
    <div
      ref={register}
      className="sticky top-0 z-40 bg-card py-1 relative before:absolute before:inset-x-0 before:bottom-full before:h-2 before:bg-card before:content-[''] after:absolute after:inset-x-0 after:top-full after:h-2 after:bg-card after:content-['']"
    >
      {bar}
    </div>
  );
}
